<script setup>
import SelectOptionFactory from '@/modules/select_option/SelectOptionFactory.js'
import SelectOptionVuetify from '@/modules/select_option/SelectOptionVuetify.js'
import { translate } from '@/plugins/i18n.js'
import { debounce } from '@/services/helpers/UtilityHelper'
import { ValidationHelper } from '@/services/helpers/ValidationHelper'
import SiteConfig from '@/services/SiteConfig'
import { computed, reactive, ref, toRaw, watch } from 'vue'

const props = defineProps({
  initialAddress: Object,
  serverErrors: {
    type: Object,
    default: () => ({})
  }
})

const emits = defineEmits(['updateData', 'clear-server-error'])

const setInitialAddress = () => {
  model.title = props.initialAddress?.title
  model.gender = props.initialAddress?.gender
  model.first_name = props.initialAddress?.first_name
  model.last_name = props.initialAddress?.last_name
  model.company = props.initialAddress?.company
  model.additional = props.initialAddress?.additional
  model.street_name = props.initialAddress?.street_name
  model.street_number = props.initialAddress?.street_number
  model.zip = props.initialAddress?.zip
  model.city = props.initialAddress?.city
  model.country = props.initialAddress?.country || SiteConfig.locale
}

const model = reactive({})
setInitialAddress()

const genderRef = ref(null)
const firstNameRef = ref(null)
const lastNameRef = ref(null)
const companyRef = ref(null)
const additionalRef = ref(null)
const streetNameRef = ref(null)
const streetNumberRef = ref(null)
const zipRef = ref(null)
const cityRef = ref(null)

const titles = SelectOptionFactory.getTitleOptions()

const genderRules = [(value) => !!value || translate('validation_msg_required')]
const firstNameRule = [
  (value) => !!value || translate('validation_msg_required'),
  (value) => value.length <= 50 || translate('validation_msg_max_length', 50)
]
const lastNameRule = [
  (value) => !!value || translate('validation_msg_required'),
  (value) => value.length <= 50 || translate('validation_msg_max_length', 50)
]
const streetNameRule = [
  (value) => !!value || translate('validation_msg_required'),
  (value) => value.length <= 50 || translate('validation_msg_max_length', 50)
]
const streetNumberRule = [
  (value) => !!value || translate('validation_msg_required'),
  (value) => value.length <= 10 || translate('validation_msg_max_length', 10)
]
const zipRule = [
  (value) => !!value || translate('validation_msg_required'),
  (value) => value.length <= 10 || translate('validation_msg_max_length', 10)
]
const cityRule = [
  (value) => !!value || translate('validation_msg_required'),
  (value) => value.length <= 50 || translate('validation_msg_max_length', 50)
]

const defaultCountry = ref(
  new SelectOptionVuetify(SiteConfig.locale, translate(SiteConfig.locale))
)

const form = ref(false)
const formRef = ref(null)

const emitUpdate = debounce((newValue) => {
  emits('updateData', form.value ? toRaw(newValue) : null)
}, 100)

watch(
  model,
  (newValue) => {
    emitUpdate(newValue)
  },
  { deep: true }
)

const validate = async (resetValidation) => {
  const { valid } = await formRef.value.validate()
  if (resetValidation) {
    formRef.value.resetValidation()
  }
  return valid
}

const hasPackstation = computed(() => ValidationHelper.hasPackstation(model))

const companyHasPackstation = computed(() =>
  ValidationHelper.hasPackstationKeyword(model.company)
)

const additionalHasPackstation = computed(() =>
  ValidationHelper.hasPackstationKeyword(model.additional)
)

const streetNameHasPackstation = computed(() =>
  ValidationHelper.hasPackstationKeyword(model.street_name)
)

const firstNameHasPackstation = computed(() =>
  ValidationHelper.hasPackstationKeyword(model.first_name)
)

const lastNameHasPackstation = computed(() =>
  ValidationHelper.hasPackstationKeyword(model.last_name)
)

const cityHasPackstation = computed(() =>
  ValidationHelper.hasPackstationKeyword(model.city)
)

// Server-side field error messages (merged with Packstation disclaimers)
const genderErrorMessages = computed(() =>
  props.serverErrors?.gender ? [props.serverErrors.gender] : undefined
)

const firstNameErrorMessages = computed(() => {
  if (firstNameHasPackstation.value) return [translate('packstation_disclaimer')]
  if (props.serverErrors?.first_name) return [props.serverErrors.first_name]
  return undefined
})

const lastNameErrorMessages = computed(() => {
  if (lastNameHasPackstation.value) return [translate('packstation_disclaimer')]
  if (props.serverErrors?.last_name) return [props.serverErrors.last_name]
  return undefined
})

const companyErrorMessages = computed(() => {
  if (companyHasPackstation.value) return [translate('packstation_disclaimer')]
  if (props.serverErrors?.company) return [props.serverErrors.company]
  return undefined
})

const additionalErrorMessages = computed(() => {
  if (additionalHasPackstation.value)
    return [translate('packstation_disclaimer')]
  if (props.serverErrors?.additional) return [props.serverErrors.additional]
  return undefined
})

const streetNameErrorMessages = computed(() => {
  if (streetNameHasPackstation.value)
    return [translate('packstation_disclaimer')]
  if (props.serverErrors?.street_name) return [props.serverErrors.street_name]
  return undefined
})

const streetNumberErrorMessages = computed(() =>
  props.serverErrors?.street_number
    ? [props.serverErrors.street_number]
    : undefined
)

const zipErrorMessages = computed(() =>
  props.serverErrors?.zip ? [props.serverErrors.zip] : undefined
)

const cityErrorMessages = computed(() => {
  if (cityHasPackstation.value) return [translate('packstation_disclaimer')]
  if (props.serverErrors?.city) return [props.serverErrors.city]
  return undefined
})

const countryErrorMessages = computed(() =>
  props.serverErrors?.country ? [props.serverErrors.country] : undefined
)

// Auto-clear server errors when the user edits the relevant field
let previousModel = { ...model }
watch(
  model,
  (newVal) => {
    if (!props.serverErrors) return
    for (const key of Object.keys(props.serverErrors)) {
      if (newVal[key] !== previousModel[key]) {
        emits('clear-server-error', key)
      }
    }
    previousModel = { ...newVal }
  },
  { deep: true }
)

// Scroll to first server-errored field when errors arrive
watch(
  () => props.serverErrors,
  (errors) => {
    if (!errors || Object.keys(errors).length === 0) return
    const fieldRefMap = {
      gender: genderRef,
      first_name: firstNameRef,
      last_name: lastNameRef,
      company: companyRef,
      additional: additionalRef,
      street_name: streetNameRef,
      street_number: streetNumberRef,
      zip: zipRef,
      city: cityRef
    }
    for (const key of Object.keys(errors)) {
      const fieldRef = fieldRefMap[key]
      if (fieldRef?.value?.$el) {
        fieldRef.value.$el.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        })
        break
      }
    }
  },
  { deep: true }
)

defineExpose({
  validate,
  isValid: form.value,
  reset: () => setInitialAddress(),
  getModel: () => toRaw(model)
})
</script>

<template>
  <VForm ref="formRef" v-model="form">
    <div class="v-row">
      <div class="v-col-12 v-col-sm-6">
        <VRadioGroup
          ref="genderRef"
          v-model="model.gender"
          :rules="genderRules"
          :error-messages="genderErrorMessages"
          class="pt-0"
          inline
        >
          <VRadio
            :label="translate('gender_female_label')"
            value="female"
            :ripple="false"
          />
          <VRadio
            :label="translate('gender_male_label')"
            value="male"
            :ripple="false"
          />
        </VRadioGroup>
      </div>
      <div class="v-col-12 v-col-sm-6">
        <VSelect
          :items="titles"
          :label="translate('opc_address_title_label')"
          v-model="model.title"
          autocomplete="honorific-prefix"
          outlined
          clearable
        />
      </div>
    </div>
    <div class="v-row">
      <div class="v-col-12 v-col-sm-6">
        <VTextField
          ref="firstNameRef"
          :aria-label="translate('opc_address_firstname_label')"
          :label="translate('opc_address_firstname_label')"
          :rules="firstNameRule"
          v-model="model.first_name"
          required
          autocomplete="given-name"
          name="given-name"
          :error-messages="firstNameErrorMessages"
          :append-inner-icon="
            firstNameHasPackstation ? 'custom:infoFilled' : undefined
          "
          @keydown.enter="lastNameRef?.focus()"
        />
      </div>
      <div class="v-col-12 v-col-sm-6">
        <VTextField
          ref="lastNameRef"
          :aria-label="translate('opc_address_lastname_label')"
          :label="translate('opc_address_lastname_label')"
          :rules="lastNameRule"
          v-model="model.last_name"
          autocomplete="family-name"
          name="family-name"
          :error-messages="lastNameErrorMessages"
          :append-inner-icon="
            lastNameHasPackstation ? 'custom:infoFilled' : undefined
          "
          @keydown.enter="companyRef?.focus()"
        />
      </div>
    </div>
    <div class="v-row">
      <div class="v-col-12 v-col-sm-6">
        <VTextField
          ref="companyRef"
          :aria-label="translate('opc_address_company_label')"
          :label="translate('opc_address_company_label')"
          v-model="model.company"
          autocomplete="organization"
          name="organization"
          :error-messages="companyErrorMessages"
          :append-inner-icon="
            companyHasPackstation ? 'custom:infoFilled' : undefined
          "
          @keydown.enter="additionalRef?.focus()"
        />
      </div>
      <div class="v-col-12 v-col-sm-6">
        <VTextField
          ref="additionalRef"
          :aria-label="translate('opc_address_additional_label')"
          :label="translate('opc_address_additional_label')"
          v-model="model.additional"
          autocomplete="address-line2"
          name="address-line2"
          :error-messages="additionalErrorMessages"
          :append-inner-icon="
            additionalHasPackstation ? 'custom:infoFilled' : undefined
          "
          @keydown.enter="streetNameRef?.focus()"
        />
      </div>
    </div>
    <div class="v-row">
      <div class="v-col-12 v-col-sm-8">
        <VTextField
          ref="streetNameRef"
          :aria-label="translate('opc_address_street_name_label')"
          :label="translate('opc_address_street_name_label')"
          :rules="streetNameRule"
          v-model="model.street_name"
          autocomplete="address-line1"
          name="address-line1"
          :error-messages="streetNameErrorMessages"
          :append-inner-icon="
            streetNameHasPackstation ? 'custom:infoFilled' : undefined
          "
          @keydown.enter="streetNumberRef?.focus()"
        />
      </div>
      <div class="v-col-12 v-col-sm-4">
        <VTextField
          ref="streetNumberRef"
          :aria-label="translate('opc_address_street_number_label')"
          :label="translate('opc_address_street_number_label')"
          :rules="streetNumberRule"
          v-model="model.street_number"
          autocomplete="off"
          name="street-number"
          :error-messages="streetNumberErrorMessages"
          @keydown.enter="zipRef?.focus()"
        />
      </div>
    </div>
    <div class="v-row">
      <div class="v-col-12 v-col-sm-4">
        <VTextField
          ref="zipRef"
          :aria-label="translate('opc_address_zip_label')"
          :label="translate('opc_address_zip_label')"
          :rules="zipRule"
          v-model="model.zip"
          autocomplete="postal-code"
          name="postal-code"
          outlined
          :error-messages="zipErrorMessages"
          @keydown.enter="cityRef?.focus()"
        />
      </div>
      <div class="v-col-12 v-col-sm-8">
        <VTextField
          ref="cityRef"
          :aria-label="translate('opc_address_city_label')"
          :label="translate('opc_address_city_label')"
          :rules="cityRule"
          v-model="model.city"
          autocomplete="address-level2"
          name="address-level2"
          :error-messages="cityErrorMessages"
          :append-inner-icon="
            cityHasPackstation ? 'custom:infoFilled' : undefined
          "
          @keydown.enter="cityRef?.blur()"
        />
      </div>
    </div>
    <div class="v-row">
      <div class="v-col-7 v-col-sm-6">
        <VSelect
          :items="[defaultCountry]"
          :aria-label="translate('opc_address_country_label')"
          :label="translate('opc_address_country_label')"
          v-model="model.country"
          autocomplete="country"
          name="country"
          disabled
          :error-messages="countryErrorMessages"
        />
      </div>
      <div class="v-col-5 v-col-sm-6 text-right">
        {{ translate('opc_mandatory_label') }}
      </div>
    </div>
  </VForm>
</template>
